
import { useT } from 'apprise-frontend-core/intl/language'
import { useL, useMultilang } from 'apprise-frontend-core/intl/multilang'
import { classname, Debugged, Styled } from 'apprise-ui/component/model'
import { FieldInfo } from 'apprise-ui/field/model'
import { ValidationStatus } from 'apprise-ui/field/validation'
import { Tipped } from 'apprise-ui/tooltip/model'
import { Tip } from 'apprise-ui/tooltip/tip'
import React, { createContext, CSSProperties, Fragment, useContext } from 'react'
import './namedproperty.scss'


export type NamedPropertyProps<T = any> = React.PropsWithChildren<Debugged & Styled & Partial<{

    name: React.ReactNode

    nameAlign: 'left' | 'right' | 'center'
    nameStyle: CSSProperties

    valueAlign: 'left' | 'right' | 'center'
    valueSuffix: React.ReactNode
    valueStyle: CSSProperties

    previously: T
    currently: T
    showChangeinTooltip: boolean | ((previous: T, current: T) => Tipped['tip'])
    highlightMode: 'change' | 'match' | 'none'
    undefinedAsEmpty: boolean
    trackCase: boolean

    noFixedHeight: boolean

    status: ValidationStatus | FieldInfo

}>>

export const NamedProperty = <T extends any = any>(props: NamedPropertyProps<T>) => {

    const t = useT()
    const l = useL()
    const ml = useMultilang()

    const ctx = useContext(NamedPropertyContext)

    const { className, style, name, nameStyle, valueStyle, children, showChangeinTooltip, undefinedAsEmpty, currently, previously, highlightMode = 'change', trackCase = false, noFixedHeight, nameAlign = 'left', valueAlign = 'left', valueSuffix, status = 'success' } = { ...ctx, ...props }

    const normalize = (value: unknown) => {
        if (value == null || value === undefined) return undefined
        if (typeof value === 'string' && !trackCase) return value.toLowerCase()
        return value
    }

    const prevNorm = normalize(previously)
    const currNorm = normalize(currently)

    const previousExists = prevNorm !== undefined
    const currentExists = currNorm !== undefined

    // show highlight if we have a previous value or we should treat undefined as a value
    const showHighlight = highlightMode !== 'none' && (previousExists || undefinedAsEmpty)

    const highlighted = showHighlight && (highlightMode === 'change'
        ? ((currentExists || previousExists) && currNorm !== prevNorm)
        : (prevNorm === currNorm))

    const info = typeof status === 'string' ? { type: status } as FieldInfo : status

    props.debug && console.log({ currently, previously, highlighted, name, highlightMode })

    const value = children && <Fragment>
        {<div className={classname('property-value', `align-${valueAlign}`)} style={valueStyle}>
            <div className='value-content'>{ml.test(children) ? l(children) : children}</div>

            {valueSuffix !== undefined
                && <div className='value-suffix'>{valueSuffix}</div>
            }
        </div>}
    </Fragment>

    return <div className={classname('named-property-wrapper', `property-${info.status}`)} >

        <Tip tip={info.msg} tipPlacement='bottom' tipType='error'>
            <div className={classname('named-property', highlighted && 'property-highlighted', noFixedHeight && 'auto-height', className)} style={style}>

                {name !== undefined && <div className={classname('property-name', `align-${nameAlign}`)} style={nameStyle}>
                    <div>{ml.test(name) ? l(name) : name}</div>
                </div>}

                {highlighted && showChangeinTooltip ?

                    <Tip tip={showChangeinTooltip === true ? t('ui.namedproperty_previously_tip', { previously }) : showChangeinTooltip(previously, currently)}>{value}</Tip>

                    : value
                }

            </div>
        </Tip>
    </div>

}


export const NamedPropertyContext = createContext<NamedPropertyProps>({})